import { json } from "react-router-dom";

import {
  API_FEATURE_FLAG,
  API_SYSTEM_INFO,
  API_USER,
  API_USER_AUTHORITIES,
  API_USER_PREFERENCES,
  APPLICATION_TEXT,
  HEADER_KEY_OF_ANTI_CSRF_TOKEN,
  ROUTES_DELETED_USER,
  ROUTES_DISALLOWED_USER,
  ROUTES_UNAUTHORIZED_USER,
} from "@utils/Constants";
import { checkDomainIsAllowedToGo, getRemoteURL, headerOptions, secureHeaderOptions } from "@utils/HttpHelpers";
import { mockFeatureFlags } from "@utils/mocks/MockFeatureFlag";

export async function loader(): Promise<Response> {
  return new Promise<Response>((resolve, reject) => {
    const isLocal = import.meta.env.VITE_ENVIRONMENT === "local";
    getUserDataOrLoginWithSCP()
      .then(async (userDetails) => {
        try {
          const [userPreferencesResponse, systemInfoResponse, authorizationResponse, featureFlagsResponse] =
            await Promise.all([
              fetch(getRemoteURL(API_USER_PREFERENCES), headerOptions),
              fetch(getRemoteURL(API_SYSTEM_INFO), headerOptions),
              fetch(getRemoteURL(API_USER_AUTHORITIES), headerOptions),
              fetch(getRemoteURL(API_FEATURE_FLAG), headerOptions),
            ]);

          if (
            !userPreferencesResponse.ok ||
            !systemInfoResponse.ok ||
            !authorizationResponse.ok ||
            !featureFlagsResponse.ok
          ) {
            throw new Response("Internal Server Error", { status: 500 });
          }

          const [userPreferences, systemInfo, authorization, featureFlags] = await Promise.all([
            userPreferencesResponse.json(),
            systemInfoResponse.json(),
            authorizationResponse.json(),
            featureFlagsResponse.json(),
          ]);

          const uniqueAuthorization = Array.from(
            new Map(
              authorization.map((item: { category: string; subcategory: string; authority: string }) => [
                `${item.category}|${item.subcategory}|${item.authority}`,
                item,
              ])
            ).values()
          );
          resolve(
            json(
              {
                user: { ...userDetails, ...userPreferences },
                authorization: uniqueAuthorization,
                hepConfig: {
                  hostCarrierCurrency: "SAR",
                },
                systemInfo,
                featureFlags: isLocal ? mockFeatureFlags : featureFlags.data,
              },
              { status: 200 }
            )
          );
        } catch (error) {
          reject(error);
        }
      })
      .catch((reason) => {
        if (reason instanceof XMLHttpRequest) {
          if (reason.status === 403) {
            if (reason.responseText && reason.responseText.includes(APPLICATION_TEXT.ACCOUNT_DELETION_MESSAGE)) {
              if (!window.location.href.endsWith(ROUTES_DELETED_USER)) {
                window.location.replace(ROUTES_DELETED_USER);
              }
            } else if (
              reason.responseText &&
              reason.responseText.includes("Community Portal: User does not have active application in his profile")
            ) {
              if (!window.location.href.endsWith(ROUTES_DISALLOWED_USER)) {
                window.location.replace(ROUTES_DISALLOWED_USER);
              }
            } else {
              if (!window.location.href.endsWith(ROUTES_UNAUTHORIZED_USER)) {
                window.location.replace(ROUTES_UNAUTHORIZED_USER);
              }
            }
            resolve(json({ user: {}, authorization: [] }, { status: 200 }));
          } else {
            reject(new Response("Internal Server Error", { status: reason.status }));
          }
        } else {
          reject(reason);
        }
      });
  });
}

/**
 * get user data for already authenticated users
 * go to Sabre Community Portal to log in to system for unauthenticated users
 */
async function getUserDataOrLoginWithSCP(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    const xhttp = new XMLHttpRequest();
    xhttp.responseType = "text";
    xhttp.onload = (): void => {
      if (xhttp.status === 200) {
        if (
          xhttp.responseURL &&
          !xhttp.responseURL.startsWith(window.location.origin) &&
          xhttp.responseText.includes("Community Portal")
        ) {
          if (checkDomainIsAllowedToGo(xhttp.responseURL)) {
            // Replace API endpoint url with current page url
            const communityPortalUrl = xhttp.responseURL.split("?goto=")[0] + "?goto=" + window.location.href;
            // Redirect to Sabre Community Portal
            window.location.replace(communityPortalUrl);
          } else {
            console.log("Invalid domain to login!");
          }
          reject(xhttp);
        } else {
          if (secureHeaderOptions.headers) {
            (secureHeaderOptions as any).headers[HEADER_KEY_OF_ANTI_CSRF_TOKEN] =
              xhttp.getResponseHeader(HEADER_KEY_OF_ANTI_CSRF_TOKEN);
          }
          resolve(JSON.parse(xhttp.responseText));
        }
      } else {
        reject(xhttp);
      }
    };
    xhttp.onerror = (): void => {
      reject(xhttp);
    };
    xhttp.open("GET", getRemoteURL(API_USER), true);
    xhttp.send();
  });
}
